import { Flex, Link } from '@radix-ui/themes';
import { CommonDialog } from 'components/common/dialogs';
import { CommonFileInput } from 'components/common/form/file';
import { HittersContext } from 'contexts/hitters.context';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { t } from 'i18next';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { TextMime } from 'lib_ts/enums/files.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IHitterUpsert } from 'lib_ts/interfaces/i-hitter';
import { first } from 'lodash';
import { useContext, useState } from 'react';

export const CsvUploadDialog = (props: { onClose: () => void }) => {
  const { uploadCsv } = useContext(HittersContext);
  const playerAppEnabled = useFeatureFlag('player-app');

  const [file, setFile] = useState<File>();

  return (
    <CommonDialog
      identifier="HitterLibraryCsvDialog"
      width={RADIX.DIALOG.WIDTH.SM}
      title="CSV Upload"
      content={
        <Flex direction="column" gap="2">
          <CommonFileInput
            id="HitterLibraryCsvFile"
            label="File"
            placeholder="Upload a .csv file"
            acceptedTypes={[TextMime.CSV]}
            filename={file?.name}
            onChange={async (files: File[]) => {
              setFile(first(files));
            }}
          />

          <Link
            className="cursor-pointer"
            onClick={() => {
              type IHitterKey = keyof IHitterUpsert;
              const csvColumns: IHitterKey[] = [
                '_id',
                'customID',
                'name',
                'level',
                'height_ft',
                'side',
                'safety_buffer',
                // only allow users to upload a phone number if the player app is enabled
                ...(playerAppEnabled ? (['phone'] as const) : []),
              ];

              const samples: Partial<Record<IHitterKey, string>>[] = [
                {
                  _id: 'ABCD-1234',
                  name: 'John Existing',
                  level: 'AAA',
                  height_ft: '6.75',
                  side: 'RHH',
                  safety_buffer: 'Low',
                  ...(playerAppEnabled ? { phone: '+14155551234' } : {}),
                },
                {
                  name: 'John New',
                  level: 'AA',
                  height_ft: '6',
                  side: 'LHH',
                  safety_buffer: 'Medium',
                },
              ];

              const table = [
                csvColumns,
                // double check that we're mapping the columns to the samples correctly
                ...samples.map((sample) =>
                  csvColumns.map((column) => sample[column] ?? '')
                ),
              ];

              const csvString = table.map((row) => row.join(',')).join('\n');

              const blob = new Blob([csvString], { type: 'text/csv' });
              MiscHelper.saveAs(blob, 'sample-import-hitters.csv');
            }}
          >
            {t('videos.download-a-sample-csv-file')}
          </Link>
        </Flex>
      }
      buttons={[
        {
          label: 'common.import',
          color: RADIX.COLOR.ACCENT,
          variant: 'solid',
          disabled: !file,
          onClick: async () => {
            if (!file) {
              return;
            }

            // upload the file to the server and get the records upserted
            const upserted = await uploadCsv(file);

            if (!upserted || upserted.length === 0) {
              return;
            }

            // close the dialog if successful
            props.onClose();
          },
        },
      ]}
      onClose={props.onClose}
    />
  );
};
